/**
 * The "From" half of Move VM: which of this customer's machines is changing hands?
 *
 * Selects in place. Picking a row reports it to the page, which keeps the receiving side on the same
 * screen — there is no route per step any more.
 *
 * A machine the backend would refuse is shown greyed with the reason on it rather than hidden: an
 * operator looking for a VM that is missing from the list learns nothing, and the reason is what
 * tells them what to fix.
 */

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Copy, Search, Server } from "lucide-react";
import { toast } from "sonner";

import { useResources } from "@/hooks/useResourceQueries";
import { ResourceStatusBadge } from "@/components/resources/ResourceStatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { Resource } from "@/types/admin";

/**
 * How many of the customer's VMs are listed in one go.
 *
 * 100 is the ceiling `GetResourcesQueryValidator` enforces, not a preference — asking for more is
 * a 422 and an empty picker. A customer with more machines than this is told so and searches
 * instead of scrolling.
 */
const PAGE_SIZE = 100;

/**
 * Why this machine cannot be moved, or null when it can.
 *
 * Mirrors the state guards in `TransferResourceOwnershipCommandHandler.Reject`, worded for the
 * person reading them. Deciding it here only saves the operator a round trip; the backend still
 * refuses on its own.
 */
function refusalFor(resource: Resource): string | null {
  if (!["Running", "Stopped", "Suspended"].includes(resource.status)) {
    return `${resource.status} — only a Running, Stopped or Suspended machine can be moved`;
  }

  if (!resource.providerResourceId?.trim()) {
    return "No machine on record — there is nothing to move";
  }

  return null;
}

function specLine(resource: Resource): string {
  const parts = [
    resource.vcpu ? `${resource.vcpu} vCPU` : null,
    resource.memoryMb
      ? `${resource.memoryMb >= 1024 ? `${Math.round(resource.memoryMb / 1024)} GB` : `${resource.memoryMb} MB`} RAM`
      : null,
    resource.diskSizeGb ? `${resource.diskSizeGb} GB disk` : null,
    resource.operatingSystem,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" · ") : "Specification not recorded";
}

/**
 * A value on a machine row the operator may want elsewhere — a UUID for a ticket, an IP for SSH.
 * Selectable as text, with a copy button that does not also select the row.
 */
function CopyValue({
  value,
  copyValue,
  label,
}: {
  value: string;
  /** What lands on the clipboard when it differs from what is shown. */
  copyValue?: string;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="select-text font-mono">{value}</span>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        title={`Copy ${label}`}
        className="cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-panel hover:text-foreground"
        onClick={(event) => {
          // The row behind this selects the machine; copying must not.
          event.stopPropagation();
          navigator.clipboard.writeText(copyValue ?? value);
          toast.success(`${label} copied`);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

export interface TransferMachinePickerProps {
  /** The customer whose machines are listed — the one losing a VM. */
  tenantId: string;
  tenantName?: string;
  selectedId: string | null;
  /**
   * Selected by itself once the list loads: the machine named in the URL the page was opened with.
   * Ignored when that machine cannot be moved, so a refusal is never pre-selected.
   */
  preselectId?: string;
  onSelect: (machine: Resource) => void;
  /** Locks the whole picker while a transfer is being applied elsewhere on the page. */
  disabled?: boolean;
}

export function TransferMachinePicker({
  tenantId,
  tenantName,
  selectedId,
  preselectId,
  onSelect,
  disabled,
}: TransferMachinePickerProps) {
  const [search, setSearch] = useState("");

  // Filtered client-side: a customer's machine count is below the page size in every real case, and
  // a round trip per keystroke would only make the list flicker. The one case it does not cover —
  // more machines than one page holds — is called out under the search box rather than left silent.
  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } =
    useResources({
      tenantId,
      type: "VirtualMachine",
      page: 1,
      pageSize: PAGE_SIZE,
    });

  const rows = data?.data;

  useEffect(() => {
    if (!preselectId || selectedId) return;
    const row = rows?.find((candidate) => candidate.id === preselectId);
    if (row && refusalFor(row) === null) onSelect(row);
  }, [rows, preselectId, selectedId, onSelect]);

  const machines = useMemo(() => {
    const list = rows ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;

    return list.filter((row) =>
      [row.name, row.providerResourceId, row.publicIpAddress, row.privateIpAddress]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  const movable = machines.filter((row) => refusalFor(row) === null);
  const total = rows?.length ?? 0;

  // `isPlaceholderData` means what's in `data` right now is the PREVIOUS tenant's page, kept on
  // screen by `useResources`' `placeholderData` so paging/filtering doesn't blank the table. Here
  // the identity of the tenant itself just changed, so those rows belong to the customer the
  // operator switched away from — showing them as clickable would let a machine be moved off the
  // wrong customer's account. Treat it exactly like the first load until this tenant's own answer
  // arrives.
  if (isLoading || isPlaceholderData) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>The machines could not be listed</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{error instanceof Error ? error.message : "Try again in a moment."}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {isFetching && <Spinner className="mr-2" />}
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (total === 0) {
    return (
      <div className="rounded-lg border border-dashed border-hairline-strong p-8 text-center">
        <Server className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="mt-2 text-sm font-medium">
          {tenantName ?? "This customer"} has no virtual machines
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name, VM UUID or IP"
            aria-label="Search this customer's machines"
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {movable.length} of {machines.length} can be moved
          {(data?.pagination?.totalItems ?? 0) > total &&
            ` · showing the first ${total} of ${data!.pagination.totalItems} — search to narrow it down`}
        </p>
      </div>

      {machines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-hairline-strong p-6 text-center text-sm text-muted-foreground">
          No machine matches “{search.trim()}”.
        </p>
      ) : (
        <ul className="scrollbar-thin max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {machines.map((machine) => {
            const refusal = refusalFor(machine);
            // `disabled` locks the whole picker (a transfer is in flight for this tenant) without
            // itself being a reason the backend would refuse, so it must not print as one below.
            const blocked = refusal !== null || !!disabled;
            const selected = machine.id === selectedId;

            return (
              <li key={machine.id}>
                {/* A div with a button role rather than a <button>: the UUID and IP carry copy
                    buttons of their own, and a button cannot contain another. */}
                <div
                  role="button"
                  tabIndex={blocked ? -1 : 0}
                  aria-pressed={selected}
                  aria-disabled={blocked}
                  onClick={() => {
                    if (!blocked) onSelect(machine);
                  }}
                  onKeyDown={(event) => {
                    if (blocked || event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(machine);
                    }
                  }}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    blocked
                      ? "cursor-not-allowed border-hairline bg-panel-2 opacity-60"
                      : selected
                        ? "cursor-pointer border-primary bg-panel-2"
                        : "cursor-pointer border-hairline bg-panel hover:border-primary/40 hover:bg-panel-2",
                  )}
                >
                  <Server className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />

                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{machine.name}</span>
                      <ResourceStatusBadge status={machine.status} />
                    </div>

                    <p className="truncate text-sm text-muted-foreground">
                      {specLine(machine)}
                    </p>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {machine.providerResourceId ? (
                        <CopyValue
                          value={machine.providerResourceId}
                          label="VM UUID"
                        />
                      ) : (
                        <span className="font-mono">no VM UUID</span>
                      )}
                      {machine.publicIpAddress && (
                        // Shown as recorded (it may carry a /24), copied as the bare address — the
                        // form an SSH client or a ticket actually wants.
                        <CopyValue
                          value={machine.publicIpAddress}
                          copyValue={machine.publicIpAddress.split("/")[0]}
                          label="IP"
                        />
                      )}
                    </div>

                    {refusal && (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-warn-ink">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        {refusal}
                      </p>
                    )}
                  </div>

                  {selected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
